import React, {
  RefObject,
  useRef,
  useState,
  MouseEvent,
  useEffect,
} from 'react';
import clsx from 'clsx';
import {
  FieldError,
} from 'react-hook-form';
import isNil from 'lodash/isNil';
import Popover from '@mui/material/Popover';
import ChevronDownIcon from '@mui/icons-material/ArrowDropDown';
import ToolTip from 'core/components/Tooltip';
import Pill from 'core/components/Pill';
import Lookup, {
  LookupOptionType,
} from 'core/components/Lookup/Generic';
import Button from 'pages/Dashboard/components/Button';
import {
  FilterLookupTranslator,
} from 'core/Filters/types';
import {
  FilterableSettingDTO,
} from 'dtos/filterableSettingDTO';

type LookupWrapperProps = {
  value?: LookupOptionType[];
  id: string;
  invalid?: boolean;
  label: string;
  onChange: (...event: any[]) => void;
  error: FieldError;
  lookupTranslator?: FilterLookupTranslator;
  defaultValue: any[];
} & Pick<FilterableSettingDTO, 'minSearchSize' | 'defaultPageSize'>;

const removeItemFromList = (
  value: LookupOptionType[],
  id: number | undefined | null | string,
  lookupTranslator?: FilterLookupTranslator,
) => {
  const { idField } = lookupTranslator ?? {};
  const index = value.findIndex((option) => option?.[idField ?? ''] === id);

  return [
    ...value.slice(0, index),
    ...value.slice(index + 1),
  ];
};

type SelectedOptionsProps = {
  selectedOptions: LookupOptionType[];
  displayNameField?: string | null;
  idField?: string | null;
  onRemove?: (id?: string | number | null) => void;
  isOpen: boolean;
};

function SelectedOptions({
  selectedOptions,
  displayNameField,
  idField,
  onRemove,
  isOpen,
}: SelectedOptionsProps) {
  const [option, ...remainder] = selectedOptions;

  return (
    <div className="flex flex-wrap space-x-1 font-normal">
      {selectedOptions.length > 0 ? (
        <>
          <Pill
            label={option?.[displayNameField ?? '']}
            id={option?.[idField ?? '']}
            onRemove={onRemove}
          />
          {selectedOptions.length > 1 && (
          <ToolTip
            title={(
              <div className="flex flex-col space-y-2 p-2">
                {remainder.map((option, index) => (
                  <Pill
                    key={option?.[idField ?? ''] ?? `${option?.[displayNameField ?? '']}-${index}`}
                    label={option?.[displayNameField ?? '']}
                    id={option?.[idField ?? '']}
                    onRemove={onRemove}
                  />
                ))}
              </div>
          )}
            isInteractive
          >
            <span className="!cursor-pointer !pointer-events-auto">
              <Pill label={`+${remainder.length}`} />
            </span>
          </ToolTip>
          )}
        </>
      ) : (<span className="text-black font-semibold">Select</span>)}
      <div className="!absolute top-0.5 right-1.5 !p-0.5 cursor-pointer">
        <ChevronDownIcon
          className={clsx('!w-6 !h-6 text-gray-500', {
            'rotate-180': isOpen,
          })}
        />
      </div>

    </div>
  );
}

export default function LookupWrapper({
  value,
  id,
  invalid = false,
  label,
  error,
  lookupTranslator,
  defaultValue,
  minSearchSize,
  defaultPageSize,
  ...props
}: LookupWrapperProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { idField, displayNameField } = lookupTranslator ?? {};
  const lookupRef = useRef<HTMLInputElement>(null);

  const togglePopover = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const closePopover = () => {
    setAnchorEl(null);
  };

  const isOpen = !isNil(anchorEl);

  const selectedOptions: LookupOptionType[] = value ?? [];

  const onRemove = (id?: number | string | null) => {
    const newValue = removeItemFromList(
      selectedOptions,
      id,
      lookupTranslator,
    );
    props.onChange(newValue);
  };

  useEffect(() => {
    if (!isNil(anchorEl)) {
      lookupRef?.current?.focus();
    }
  }, [selectedOptions, anchorEl]);

  return (
    <div className="w-full flex">
      <Button
        variant="outlined"
        type="button"
        color="primary"
        onClick={togglePopover}
        className={clsx(
          '!p-3 w-full !max-w-full flex-1 text-left !justify-start hover:!bg-white !h-8 hover:!border-gray-600',
          'focus-visible:ring-2 focus-visible:ring-default-input-focused',
          {
            '!border-gray-400': isOpen,
          },
        )}
        disableRipple
      >
        <SelectedOptions
          selectedOptions={selectedOptions}
          displayNameField={displayNameField}
          onRemove={onRemove}
          idField={idField}
          isOpen={isOpen}
        />
      </Button>
      <Popover
        id="filter-lookup"
        anchorEl={anchorEl}
        open={isOpen}
        onClose={closePopover}
        slotProps={{
          paper: {
            className: '!overflow-visible !shadow-lg',
          },
        }}
        anchorOrigin={{
          vertical: (anchorEl?.offsetHeight ?? 0) + 4,
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Lookup
          {...props}
          value={value}
          key={id}
          error={!isNil(error)}
          id={id}
          multiple
          placeholder={label}
          className="!mb-0 !w-[288px] relative"
          inputRef={lookupRef as RefObject<HTMLInputElement>}
          lookupTranslator={lookupTranslator}
          selectOnFocus
          renderTags={() => null}
          minSearchSize={minSearchSize}
          defaultPageSize={defaultPageSize}
        />
      </Popover>

    </div>

  );
}
